/**
 * The account-access watch (US #1814, Task #1818 A24b). LIVE: not behind any switch in the SPA.
 * It watches for the BFF's two refusal codes (an org-less `ACCOUNT_DISABLED` carrying `scope:
 * 'every_organisation'` is recorded as `ALL_ORGANISATIONS_DISABLED`, Task #3008) and also holds the
 * SPA's own stops ({@link recordNoOrganisation}, {@link recordAllOrganisationsDisabled});
 * {@link clearAccountBlock} lifts any, {@link clearNoOrganisation} only the no-organisation one.
 *
 * With the BFF's account check on (`ACCOUNT_ACCESS_CHECK=enforce`, Bug #2453; it ships OFF),
 * the BFF refuses EVERY authenticated `/api` request with 403 when the user's membership of the
 * token's organisation is disabled (`code: 'ACCOUNT_DISABLED'`, per organisation since #2730) or
 * was removed (`code: 'MEMBERSHIP_REMOVED'`). Removed members hit this as soon as ops switches the check
 * on. The SPA must then stop and say so, instead of every page failing in its own way. With the
 * check off the BFF never sends these codes, so this watch never fires.
 *
 * ## Why here, and why `fetch`
 *
 * The SPA has no central API client. Composables, services and stores each call `fetch`
 * themselves (with their own local `apiFetch` helpers), so there is no one client to hook.
 * The one thing every one of those calls shares is the global `fetch`, so this module wraps it
 * ONCE, at boot (`main.ts`), before auth initialises, which means the first
 * `/user-roles/me` after sign-in is already observed. `useDevConsole` wraps `fetch` the same
 * way for its network log, and the two compose.
 *
 * ## What it does, and what it must never do
 *
 *  - Every response passes through UNCHANGED, to the caller that asked for it. Only a 403
 *    FROM OUR OWN API (the `VITE_BFF_URL` base, see `resolveApiBases`) with a JSON body is
 *    looked at, and only through a clone, so the caller's body is unread. A 403 from Auth0,
 *    Sentry or any other origin or path is never read at all.
 *  - A 403 WITHOUT one of the two codes (the ordinary role 403s) is not a block. Neither is
 *    the 503 `ACCOUNT_CHECK_UNAVAILABLE`: that is a temporary outage, and each page's existing
 *    error handling already covers it.
 *  - Once blocked, the state is settled until a full page load (signing out reloads), a
 *    successful sign-in callback or a successful invite accept (both lift it). A later
 *    refusal of the same kind changes nothing; in this client `ACCOUNT_DISABLED`
 *    replaces a recorded `MEMBERSHIP_REMOVED`, never the other way round, and a recorded
 *    `ALL_ORGANISATIONS_DISABLED` is never replaced.
 *
 * `App.vue` renders the blocked screen from {@link accountBlock}, inside the app shell. No
 * router route is involved.
 *
 * ## The organisation's name (Task #1822, A28 polish)
 *
 * The token's `org_name` claim is the Auth0 organisation's short name (`rnprojects`), not
 * the name people know it by (`RN Projects`). So for both codes (a disable is per organisation
 * too, Task #2731) the block is set straight away with `org_name`, and then the organisation's
 * DISPLAY name is looked up from the public, unauthenticated `GET /public/organisations` on the SAME API base the refused
 * request went to, matched by the token's `org_id`. That call carries no token, so the live
 * access check never refuses it, and it goes through the unwrapped `fetch`, so the watch never
 * inspects its own lookup. If the lookup fails or finds nothing, `org_name` stays, and
 * without it the screen says "this organisation".
 *
 * ## The organisation a seat refusal names (#1842)
 *
 * A member with no seat is refused `GET /api/user/organisations` too, so the SPA's current-organisation
 * store may never be filled and the Seats page would address `/api/organisations/null/...`. The
 * `SEAT_REQUIRED` body therefore carries `organisationId`: the database id the BFF resolved from the
 * caller's own token. The watch records it (no name) when the store holds no id or a different
 * one: the BFF's resolution is authoritative, so a stale stored id is replaced, never kept.
 *
 * @module lib/account-access
 */

import { readonly, shallowRef } from 'vue'
import { decodeToken, type TokenClaims } from '@/utils/token.utils'
import { useCurrentOrganisationStore } from '@/stores/current-organisation.store'

/**
 * The block's reason. `ACCOUNT_DISABLED` and `MEMBERSHIP_REMOVED` are BFF refusals, noticed by the
 * fetch watch. `NO_ORGANISATION` is the odd one out: it is not a BFF refusal. The user signed in, their token
 * names no organisation and they belong to none (#2688), so there is nothing to refuse and the SPA
 * stops them itself. `auth.store` decides it once, after the roles and the organisation list load.
 * `ALL_ORGANISATIONS_DISABLED` (Task #3008) is also a SPA block code, never sent by the BFF as a `code`: the person belongs to organisations but is
 * disabled in every one. It is recorded from the BFF's org-less 403 (`ACCOUNT_DISABLED` with `scope:
 * 'every_organisation'`) or by `auth.store` from the organisation list.
 */
export type AccountBlockCode =
  | 'ACCOUNT_DISABLED'
  | 'MEMBERSHIP_REMOVED'
  | 'NO_ORGANISATION'
  | 'ALL_ORGANISATIONS_DISABLED'

export interface AccountBlock {
  code: AccountBlockCode
  /** The organisation the refused request was for: its display name when the public
   *  organisation list has it, else the token's `org_name` claim, else `null`. Names the
   *  organisation the refusal was for, for both codes (a disable is per organisation since Task
   *  #2730/#2731). */
  organisationName: string | null
}

const block = shallowRef<AccountBlock | null>(null)

const noSeat = shallowRef(false)

/** True once the BFF has refused an own-API request with `SEAT_REQUIRED` (#1848): the member holds no usable seat. */
export const seatRefused = readonly(noSeat)

/** The current block, or `null`. Read-only: only a refused response sets it. */
export const accountBlock = readonly(block)

/** Targets already wrapped, so a second install is a no-op rather than a second layer. */
const installed = new WeakSet<object>()

/**
 * Wrap `target.fetch` (the real `window` at boot) so account refusals are noticed. Idempotent.
 * Never throws, and never changes what a caller receives: the ORIGINAL `Response` is returned,
 * never the clone the watch reads.
 *
 * `bffUrl` is the API base the app builds its request URLs from, `VITE_BFF_URL` by default.
 * Only a request to that API is ever inspected. See {@link isOwnApiRequest}.
 */
export function installAccountAccessWatch(
  target: { fetch: typeof fetch } = window,
  bffUrl: string | undefined = import.meta.env.VITE_BFF_URL as string | undefined
): void {
  if (installed.has(target)) return
  installed.add(target)

  const apiBases = resolveApiBases(bffUrl)
  const inner = target.fetch.bind(target)
  target.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await inner(input, init)
    if (response.status === 403) {
      const apiBase = ownApiBaseOf(input, apiBases)
      if (apiBase) await noticeRefusal(response, input, init, apiBase, inner)
    }
    return response
  }
}

/**
 * Stop a signed-in user who belongs to no organisation (#2688). Never replaces a block the BFF
 * has already recorded: those name a real organisation and say more.
 */
export function recordNoOrganisation(): void {
  if (block.value) return
  block.value = { code: 'NO_ORGANISATION', organisationName: null }
}

/**
 * Stop a signed-in user who belongs to organisations but is disabled in every one of them
 * (Task #3008), when `auth.store` learns it from the organisation list. Never replaces a block
 * already recorded (a BFF refusal names a real organisation and says more).
 */
export function recordAllOrganisationsDisabled(): void {
  if (block.value) return
  block.value = { code: 'ALL_ORGANISATIONS_DISABLED', organisationName: null }
}

/**
 * Lift ANY recorded block. Called after a SUCCESSFUL invite accept (the person now holds a fresh
 * membership, so a stop recorded earlier, such as a BFF refusal of an org-less request while they
 * were disabled everywhere or the no-organisation stop, no longer describes them) and by
 * `auth.store` after every successful sign-in callback (a new token supersedes refusals of the
 * one it replaced). The next real refusal records itself again.
 */
export function clearAccountBlock(): void {
  block.value = null
}

/**
 * Lift the no-organisation stop once the user has joined one (#2688 / #2791): kept for callers
 * that must not lift a refusal. Clears ONLY a `NO_ORGANISATION` block. A block the BFF recorded
 * (`ACCOUNT_DISABLED`, `MEMBERSHIP_REMOVED`) is a refusal, and stays settled until a full page
 * load, a successful sign-in callback or an invite accept lifts it (see `clearAccountBlock`).
 */
export function clearNoOrganisation(): void {
  if (block.value?.code === 'NO_ORGANISATION') block.value = null
}

/**
 * Whether `path` is an invitation page (`/invite/:token`). An invitee signs in with no
 * organisation and belongs to none until they accept, so the no-organisation stop must never be
 * recorded while they are on, or being returned to, one of these (#2791 security finding).
 */
export function isInvitePath(path: string | null | undefined): boolean {
  return typeof path === 'string' && /^\/invite(?:\/|$)/i.test(path)
}

/** Test-only: the state otherwise lives until a full page load. */
export function resetAccountBlockForTests(): void {
  block.value = null
  noSeat.value = false
}

/**
 * The API bases the app's own requests can go to, resolved against the page's origin.
 *
 * Every API call is built as `VITE_BFF_URL || <fallback>`. Deployed builds set `VITE_BFF_URL`
 * to the BFF's absolute URL, which is a DIFFERENT origin from the SPA, so that URL is the only
 * base. With it unset (local dev), the code falls back to one of two bases: `'/api'` on the
 * page's own origin (the Vite proxy), or `'http://localhost:3001/api'` (the BFF directly). Both
 * are accepted then.
 */
function resolveApiBases(bffUrl: string | undefined): URL[] {
  const candidates = bffUrl ? [bffUrl] : ['/api', 'http://localhost:3001/api']
  const bases: URL[] = []
  for (const candidate of candidates) {
    try {
      bases.push(new URL(candidate, window.location.origin))
    } catch {
      // An unparseable base matches nothing, so the watch stays off rather than guessing.
    }
  }
  return bases
}

/**
 * The API base a request went to, when it went to our own API: its URL (string, `URL` or
 * `Request`, resolved against the page's origin) has the same origin as one of `apiBases`, and
 * its path is at or under that base's path. Anything else (Auth0, Sentry, a third party, a
 * non-API path on our own origin) returns `null` and is never inspected, so its body is never
 * read.
 */
function ownApiBaseOf(input: RequestInfo | URL, apiBases: URL[]): URL | null {
  let url: URL
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    url = new URL(raw, window.location.origin)
  } catch {
    return null
  }
  return (
    apiBases.find(base => {
      if (url.origin !== base.origin) return false
      const prefix = base.pathname.replace(/\/+$/, '')
      return url.pathname === prefix || url.pathname.startsWith(`${prefix}/`)
    }) ?? null
  )
}

async function noticeRefusal(
  response: Response,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  apiBase: URL,
  unwrappedFetch: typeof fetch
): Promise<void> {
  try {
    const refusal = await readRefusal(response)
    if (!refusal) return
    const { code } = refusal
    if (code === 'SEAT_REQUIRED') {
      noSeat.value = true
      const store = useCurrentOrganisationStore()
      if (refusal.organisationId && store.id !== refusal.organisationId) {
        store.set(refusal.organisationId)
      }
      return
    }
    const claims = tokenClaimsOf(input, init)
    const set = record(code, nonEmpty(claims?.org_name))
    const orgId = nonEmpty(claims?.['https://sitecompass.au/org_id']) ?? nonEmpty(claims?.org_id)
    // Not awaited: the caller gets its response now, and the name follows when it arrives.
    if (set && orgId) void upgradeToDisplayName(set, orgId, apiBase, unwrappedFetch)
  } catch {
    // Observing must never break the request it observes.
  }
}

/**
 * Replace the block's organisation name with the organisation's display name from the
 * public organisation list, if it has one for `orgId`. Leaves the block alone on any failure,
 * and if the block has changed meanwhile (e.g. upgraded to `ACCOUNT_DISABLED`).
 */
async function upgradeToDisplayName(
  set: AccountBlock,
  orgId: string,
  apiBase: URL,
  unwrappedFetch: typeof fetch
): Promise<void> {
  try {
    const url = `${apiBase.href.replace(/\/+$/, '')}/public/organisations`
    const response = await unwrappedFetch(url)
    if (!response.ok) return
    const body: unknown = await response.json()
    const organisations = (body as { organisations?: unknown } | null)?.organisations
    if (!Array.isArray(organisations)) return
    const match = organisations.find(
      (o: unknown) => (o as { auth0_org_id?: unknown } | null)?.auth0_org_id === orgId
    ) as { display_name?: unknown } | undefined
    const displayName = nonEmpty(match?.display_name)
    if (displayName && block.value === set) {
      block.value = { ...set, organisationName: displayName }
    }
  } catch {
    // The token's org_name (or "this organisation") stays.
  }
}

async function readRefusal(response: Response): Promise<{
  code: Exclude<AccountBlockCode, 'NO_ORGANISATION'> | 'SEAT_REQUIRED'
  organisationId: string | null
} | null> {
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.includes('json')) return null
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => null)
  if (!body || typeof body !== 'object') return null
  const { code, scope, organisationId } = body as {
    code?: unknown
    scope?: unknown
    organisationId?: unknown
  }
  if (code === 'ACCOUNT_DISABLED' && scope === 'every_organisation') {
    return { code: 'ALL_ORGANISATIONS_DISABLED', organisationId: null }
  }
  if (code === 'ACCOUNT_DISABLED' || code === 'MEMBERSHIP_REMOVED') {
    return { code, organisationId: null }
  }
  return code === 'SEAT_REQUIRED' ? { code, organisationId: nonEmpty(organisationId) } : null
}

/** Set the block unless it is already settled. Returns the block it set, or `null`. */
function record(code: AccountBlockCode, organisationName: string | null): AccountBlock | null {
  const current = block.value
  if (
    current &&
    (current.code === 'ACCOUNT_DISABLED' ||
      current.code === 'ALL_ORGANISATIONS_DISABLED' ||
      code === current.code)
  ) {
    return null
  }
  const next = { code, organisationName }
  block.value = next
  return next
}

/** The refused request's own bearer token names the org it was for. Decoded, not verified:
 *  the BFF already verified it, and this is only a label. */
function tokenClaimsOf(
  input: RequestInfo | URL,
  init: RequestInit | undefined
): TokenClaims | null {
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : {}))
  const authorization = headers.get('authorization') ?? ''
  if (!authorization.startsWith('Bearer ')) return null
  return decodeToken(authorization.slice('Bearer '.length))
}

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}
